import React, { useCallback, useEffect, useState } from 'react';

import { Box, Typography, Container, Grid, LinearProgress, Select, MenuItem, FormControl, InputLabel, CircularProgress, TextField, ToggleButton, ToggleButtonGroup, Pagination } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { debounce } from 'lodash';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { incidentListSelector, useUpdateFilterCallback, useUpdatePageCallback, useLoadFilterCallback, incidentTagsState, useUpdateTagCallback, selectedIncidentIdState } from '../../states/incidents';
import { selectedHistoricalDeliveryIdsState, useSelectShipmentCallback } from '../../states/shipment';
import { allClientsSelector } from '../../states/client';
import { allRegionSelector } from '../../states/region';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import IncidentListItem from './list_item';
import Button from '@mui/material/Button';
import incidentTypeList from '../../constants/incidentTypeList';

const HEADER_COLUMNS = [
  { label: 'SHIPMENT', size: { xs: 3, md: 2, lg: 1 } },
  { label: 'CLIENT', size: { xs: 2, md: 1 } },
  { label: 'CREATED', size: { xs: 2, md: 2 } },
  { label: 'DELIVERY', size: { xs: 2, md: 1 } },
  { label: 'STREET', size: 3 },
  { label: 'CITY', size: 1 },
  { label: 'STATE', size: 2 },
  { label: 'PRIORITY', size: 1 },
];

function IncidentListInner({ base }) {
  const incidentList = useRecoilValue(incidentListSelector);
  const updatePage = useUpdatePageCallback();

  const gotoPage = (p) => {
    updatePage(parseInt(p, 10));
  };

  return (
    <Container style={{ marginBottom: '20px', padding: 0 }}>
      <Box
        sx={{
          p: 1,
          bgcolor: '#f0f0f0',
          display: 'grid',
          gridTemplateColumns: { md: '1fr' },
          gap: 0.5,
        }}
      >
        <Grid container spacing={0.5} alignItems={'center'}>
          {HEADER_COLUMNS.map((column) => (
            <Grid key={column.label} size={column.size}>
              <Typography style={{ fontSize: 14, color: '#9b9b9b', fontWeight: '600' }}>
                {column.label}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {incidentList.items.map((item) => (
          <IncidentListItem base={base} key={item.id} incident={item} />
        ))}
      </Box>
      <Pagination
        showFirstButton
        size="small"
        color="primary"
        count={Math.floor((incidentList.total + incidentList.limit - 1) / incidentList.limit)}
        page={1 + incidentList.offset / incidentList.limit}
        onChange={(e, p) => gotoPage(p)}
      />
    </Container>
  );
}

function ClientFilter() {
  const allClient = useRecoilValue(allClientsSelector);
  const [client, setClient] = useState('ALL');
  const incidentTags = useRecoilValue(incidentTagsState);
  const updateIncidentTags = useUpdateTagCallback();
  const updateClientFilter = (e) => {
    const v = e.target.value;
    setClient(v);
    updateIncidentTags({ client: v === 'ALL' ? [] : [v] });
  };

  useEffect(() => {
    const tag = !incidentTags?.client || incidentTags.client.length < 1 ? 'ALL' : incidentTags.client[0];
    setClient(tag);
  }, [incidentTags]);

  return (
    <FormControl variant='standard' style={{ marginRight: '20px' }}>
      <InputLabel>Client</InputLabel>
      <Select
        value={client}
        label="Client"
        inputProps={{
          name: 'client',
          id: 'incident-client',
        }}
        onChange={updateClientFilter}
        variant='standard'
        size='small'
      >
        <MenuItem value={'ALL'}>ALL</MenuItem>
        {allClient &&
          allClient.map((client) => (
            <MenuItem key={client.id} value={`CL_${client.id}`}>
              {client.company}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
}

function RegionFilter() {
  const allRegion = useRecoilValue(allRegionSelector);
  const [region, setRegion] = useState('ALL');
  const incidentTags = useRecoilValue(incidentTagsState);
  const updateIncidentTags = useUpdateTagCallback();
  const updateRegionFilter = (e) => {
    const v = e.target.value;
    setRegion(v);
    updateIncidentTags({ region: v === 'ALL' ? [] : [v] });
  };

  useEffect(() => {
    const tag = !incidentTags?.region || incidentTags.region.length < 1 ? 'ALL' : incidentTags.region[0];
    setRegion(tag);
  }, [incidentTags]);

  return (
    <FormControl variant='standard' style={{ marginRight: '20px' }}>
      <InputLabel>Region</InputLabel>
      <Select
        value={region}
        label="Region"
        inputProps={{
          name: 'region',
          id: 'incident-region',
        }}
        onChange={updateRegionFilter}
        variant='standard'
        size='small'
      >
        <MenuItem value={'ALL'}>ALL</MenuItem>
        {allRegion &&
          allRegion.map((region) => (
            <MenuItem key={region.label} value={`RG_${region.value}`}>
              {region.label} ({region.value})
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
}

export default function IncidentList() {
  const params = useParams();
  const location = useLocation();
  const selectShipment = useSelectShipmentCallback();
  const setSelectedHistory = useSetRecoilState(selectedHistoricalDeliveryIdsState);
  const updateFilter = useUpdateFilterCallback();
  const loadFilter = useLoadFilterCallback();
  const [tags, setTags] = useState('ALL');
  const incidentTags = useRecoilValue(incidentTagsState);
  const updateIncidentTags = useUpdateTagCallback();
  const setSelectedIncidentId = useSetRecoilState(selectedIncidentIdState);
  const queryParams = new URLSearchParams(location.search || '');
  const [datePicker, setDatePicker] = useState(() => {
    const dateParam = queryParams.get('date');
    return dateParam ? moment(dateParam) : null;
  });
  const [incidentType, setType] = useState(params.type ? params.type.toUpperCase() : 'GEOCODE-FAILED');
  const [status, setStatus] = useState(queryParams.get('status') || 'CREATED');
  const [openPicker, setOpenPicker] = useState(false);
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');

  const debounceSearchInput = useCallback(
    debounce((value) => updateIncidentTags({ search: value }), 1000),
    [],
  );

  useEffect(() => {
    return () => {
      debounceSearchInput.cancel();
    };
  }, [debounceSearchInput]);

  const updateStatusFilter = (e) => {
    setStatus(e.target.value);
    updateFilter({ status: e.target.value });
  };

  const updateTypeFilter = (e) => {
    setType(e.target.value);
    updateFilter({ type: e.target.value });
  };

  const updateDPTags = (e, v) => {
    if (v != null) {
      setTags(v);
      updateIncidentTags({ dp: v === 'ALL' ? [] : [v] });
    }
  };

  const updateSearchInput = (v) => {
    setSearchInput(v);
    debounceSearchInput(v ? [`Search_${v}`] : []);
  };

  useEffect(() => {
    loadFilter();
    setSelectedHistory([]);
    selectShipment(null);
    setSelectedIncidentId(null);
  }, []);

  useEffect(() => {
    const tag = !incidentTags?.dp || incidentTags.dp.length < 1 ? 'ALL' : incidentTags.dp.indexOf('DSP') >= 0 ? 'DSP' : 'DP';
    const searchTag = incidentTags?.search?.[0] || '';
    const searchInput = searchTag.startsWith('Search_') ? searchTag.slice('Search_'.length) : '';
    setSearchInput(searchInput);
    setTags(tag);
  }, [incidentTags]);

  const handleChangeDate = (date) => {
    const dateFormat = moment(date).format('YYYY-MM-DD');
    const currentDateFormat = datePicker ? moment(datePicker).format('YYYY-MM-DD') : null;
    let dataDate = dateFormat;
    if (currentDateFormat === dateFormat) {
      dataDate = undefined;
    }

    setDatePicker(dataDate ? moment(dataDate) : null);
    updateFilter({ date: dataDate });
  };

  const goToLandingPage = () => {
    navigate(`/`);
  };

  return (
    <Container style={{ height: 'calc(100vh - 65px)' }}>
      <Container maxWidth="xl" style={{ padding: '15px', display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          color='inherit'
          onClick={() => {
            goToLandingPage();
          }}
        >
          Back to Main
        </Button>
      </Container>
      <div style={{ padding: '15px', display: 'flex', alignItems: 'center' }}>
        <Box display={'inline-flex'} style={{ gap: '8px' }}>
          <span style={{ display: 'inline-block', fontSize: '1.2em', padding: '16px 10px 0px 0px', color: '#4a4a4a' }}>Overview for</span>
          <FormControl variant='standard' style={{ marginRight: '20px' }}>
            <InputLabel id="incident-select-type">Type</InputLabel>
            <Select
              value={incidentType}
              label="Type"
              inputProps={{
                name: 'type',
                id: 'incident-type',
              }}
              onChange={updateTypeFilter}
              variant='standard'
              size='small'
            >
              {incidentTypeList.map((incident) => (
                <MenuItem key={incident.type.toUpperCase()} value={incident.type.toUpperCase()}>
                  {incident.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <FormControl variant='standard' style={{ marginRight: '20px' }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              open={openPicker}
              onOpen={() => setOpenPicker(true)}
              onClose={() => setOpenPicker(false)}
              label="Date"
              format="YYYY-MM-DD"
              value={datePicker}
              onChange={(date) => handleChangeDate(date)}
              slotProps={{
                textField: {
                  size: 'small',
                  id: 'incident-select-date',
                  InputProps: {
                    readOnly: true,
                    onClick: () => setOpenPicker(true),
                    style: { cursor: 'pointer' },
                    endAdornment: <ArrowDropDownIcon />,
                  },
                  variant: 'standard',
                },
              }}
            />
          </LocalizationProvider>
        </FormControl>
        <FormControl variant='standard' style={{ marginRight: '20px' }}>
          <InputLabel id="incident-select-status">Status</InputLabel>
          <Select
            value={status}
            label="Status"
            inputProps={{
              name: 'status',
              id: 'incident-status',
            }}
            onChange={updateStatusFilter}
            variant='standard'
            size='small'
          >
            <MenuItem value={'CREATED'} style={{ color: '#f4485e' }}>
              Unresolved
            </MenuItem>
            <MenuItem value={'DISPATCH_FOLLOWUP'}>Dispatch Follow-up Required</MenuItem>
            <MenuItem value={'UNSOLVABLE'}>UnSolvable</MenuItem>
            <MenuItem value={'RESOLVED'} style={{ color: '#75c31e' }}>
              Resolved
            </MenuItem>
          </Select>
        </FormControl>
        <React.Suspense fallback={<CircularProgress />}>
          <ClientFilter />
        </React.Suspense>
        <React.Suspense fallback={<CircularProgress />}>
          <RegionFilter />
        </React.Suspense>
        <ToggleButtonGroup color="standard" value={tags} exclusive onChange={updateDPTags} size="small" style={{ marginRight: '20px' }}>
          <ToggleButton value="ALL">ALL</ToggleButton>
          <ToggleButton value="DSP">DSP</ToggleButton>
          <ToggleButton value="DP">IC</ToggleButton>
        </ToggleButtonGroup>
      </div>
      <Box px={1}>
        <FormControl variant='standard' style={{ verticalAlign: 'bottom', width: '50%' }}>
          <TextField
            size="small"
            placeholder="Shipment ID, Tracking code, Customer name, Address"
            value={searchInput}
            fullWidth
            variant="outlined"
            onChange={(e) => updateSearchInput(e.target.value)}
          />
        </FormControl>
      </Box>
      <Box py={2} px={1}>
        <React.Suspense fallback={<LinearProgress />}>
          <IncidentListInner base={`/incident/${params.type}`} />
        </React.Suspense>
      </Box>
    </Container>
  );
}
